import { useState } from 'react';

import { OtpInitiation, OtpConfirmation } from '~features/auth/auth-by-otp';
import type {
  OtpConfirmationError,
  OtpInitiationError,
} from '~features/auth/auth-by-otp';
import {
  lastOtpRequestAtom,
  resetLastOtpRequestsAtom,
} from '~features/auth/auth-by-otp/model/otp-request-atoms';

import type { AuthApi } from '~entities/auth';
import { AUTH_API_KEY, isChallengeConfirmationError } from '~entities/auth';
import { useInitiateMutation, useConfirmMutation } from '~entities/auth';

import { isAuthError } from '~shared/api/errors';
import { useApi } from '~shared/providers/use-api';

import { useSetAtom } from 'jotai';

type Props = {
  initialPhone: string;
  onConfirmSuccess: (phoneNumber: string) => void;
};
export const MerchantLoginWidget = ({
  onConfirmSuccess,
  initialPhone,
}: Props) => {
  const authApi = useApi<AuthApi>(AUTH_API_KEY);
  const [currentStep, setCurrentStep] = useState<'initiate' | 'confirm'>(
    'initiate'
  );

  const [phoneNumber, setPhoneNumber] = useState<string>(initialPhone);
  const resetLastOtpRequests = useSetAtom(resetLastOtpRequestsAtom);
  const setLastOtpRequest = useSetAtom(lastOtpRequestAtom);

  const [initiateStepError, setInitiateStepError] =
    useState<OtpInitiationError>();
  const [confirmStepError, setConfirmStepError] =
    useState<OtpConfirmationError>();

  const initiateMutation = useInitiateMutation(authApi, {
    onError: () => {
      setInitiateStepError('unexpectedError');
    },
    onSuccess: (_d, params) => {
      setLastOtpRequest({
        phone: params.phoneNumber,
        time: new Date().getTime(),
      });
      setPhoneNumber(params.phoneNumber);
      setCurrentStep('confirm');
    },
  });

  const confirmMutation = useConfirmMutation(authApi, {
    onError: (error) => {
      if (isChallengeConfirmationError(error)) {
        setConfirmStepError('confirmationError');
        return;
      }
      if (isAuthError(error as Error)) {
        setInitiateStepError('otpAttemptsLimit');
        setConfirmStepError(undefined);
        setCurrentStep('initiate');
        return;
      }
      setConfirmStepError('unexpectedError');
    },
    onSuccess: () => {
      resetLastOtpRequests(phoneNumber);
      onConfirmSuccess(phoneNumber);
    },
  });

  return currentStep === 'initiate' ? (
    <OtpInitiation
      initialPhone={phoneNumber}
      onSubmit={(phone) => {
        setInitiateStepError(undefined);
        initiateMutation.mutate({ phoneNumber: phone });
      }}
      isLoading={initiateMutation.isLoading}
      error={initiateStepError}
    />
  ) : (
    <OtpConfirmation
      phoneNumber={phoneNumber}
      onCodeResend={async () => {
        await authApi.resendConfirmation();
      }}
      onCodeSubmit={(answer) => {
        confirmMutation.mutate({ answer });
      }}
      onCodeResendLimitError={() => {
        setConfirmStepError(undefined);
        setCurrentStep('initiate');
      }}
      onNumberChange={() => {
        setConfirmStepError(undefined);
        setCurrentStep('initiate');
      }}
      codeChecking={confirmMutation.isLoading}
      codeSubmitError={confirmStepError}
    />
  );
};
